import { forgeWorkerUrl } from '@/lib/forge-worker-client';
import { invokeEdgeFunctionStream } from '@/lib/sse-client';
import {
  type CompanyQualificationSheet,
  QUALIFICATION_SECTIONS,
  QUALIFICATION_THEMES,
  type QualificationItem,
  type QualificationStatus,
  type QualificationThemeKey,
  parseQualificationSheet,
} from '@shared/types/company-qualification';
import { briefBasis, pageBasis } from '@shared/types/company-restitution';

export type RestitutionTarget = 'brief' | QualificationThemeKey;
export type RestitutionScope = 'broad' | 'all' | 'refresh' | QualificationThemeKey;

/** How a reader sees the status of an element, from the weakest line it cites. */
export const EVIDENCE_LABELS: Record<QualificationStatus | 'none', string> = {
  validated: 'validé',
  observed: 'constaté',
  hypothesis: 'supposé',
  gap: 'inconnu',
  none: 'non sourcé',
};

export const linesById = (sheet: CompanyQualificationSheet): Map<string, QualificationItem> =>
  new Map(QUALIFICATION_SECTIONS.flatMap((key) => sheet.sections[key].map((item) => [item.id, item] as const)));

const sectionsOf = (theme: QualificationThemeKey): readonly string[] =>
  QUALIFICATION_THEMES.find(({ key }) => key === theme)?.sections ?? [];

/** The sheet changed since the brief was written. */
export const briefIsStale = (sheet: CompanyQualificationSheet): boolean =>
  Boolean(sheet.restitution.brief && sheet.restitution.brief.basis !== briefBasis(sheet));

/** The theme's lines changed since its page was written. */
export const pageIsStale = (sheet: CompanyQualificationSheet, theme: QualificationThemeKey): boolean => {
  const page = sheet.restitution.pages[theme];
  return Boolean(page && page.basis !== pageBasis(sheet, sectionsOf(theme)));
};

export interface RestitutionOutcome {
  sheet: CompanyQualificationSheet;
  version: number;
  failed: RestitutionTarget[];
}

const record = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? value as Record<string, unknown> : {};

/** Writes the restitution; each part is announced when it is ready (real progress, not an estimate). */
export const writeRestitution = async (
  scope: RestitutionScope,
  handlers: { onTargets: (targets: RestitutionTarget[]) => void; onPart: (target: RestitutionTarget, ok: boolean) => void },
): Promise<RestitutionOutcome> => {
  if (!forgeWorkerUrl) throw new Error('L’analyste n’est pas configuré sur cet environnement (VITE_FORGE_WORKER_URL).');
  let outcome: RestitutionOutcome | null = null;
  let failure: string | null = null;
  await invokeEdgeFunctionStream({
    url: forgeWorkerUrl,
    body: { action: 'analyst_restitution', scope },
    onEvent: (name, data) => {
      const payload = record(data);
      if (name === 'progress' && payload.stage === 'restitution' && Array.isArray(payload.targets)) {
        handlers.onTargets(payload.targets as RestitutionTarget[]);
      } else if (name === 'progress' && payload.stage === 'restitution_part' && typeof payload.target === 'string') {
        handlers.onPart(payload.target as RestitutionTarget, payload.ok === true);
      } else if (name === 'complete') {
        outcome = {
          sheet: parseQualificationSheet(payload.sheet).sheet,
          version: typeof payload.version === 'number' ? payload.version : 0,
          failed: Array.isArray(payload.failed) ? payload.failed as RestitutionTarget[] : [],
        };
      } else if (name === 'error') {
        failure = typeof payload.message === 'string' ? payload.message : 'La restitution n’a pas pu être rédigée.';
      }
    },
  });
  if (failure) throw new Error(failure);
  if (!outcome) throw new Error('La rédaction a été coupée. Rechargez pour voir ce qui a été écrit.');
  return outcome;
};
